import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import { color } from '../../../app/theme';
import { UrlFilterType } from '../../../app/types';
import TextField from '../../shared/TextField';
import Dropdown from '../../shared/Dropdown';
import {
  generateDateItem,
  changeSeconds,
  validateHttpsAndHttpPath,
} from '../../../app/utils';
import IconX16 from '../../../assets/images/Icons/IconX16';

const useStyles = makeStyles(
  {
    title: {
      fontSize: '15px',
      fontWeight: '500',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$3,
      marginBottom: 12,
    },
    subtitle: {
      fontSize: '15px',
      fontWeight: '400',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '21px',
      letterSpacing: 'normal',
      color: color.$3,
      marginBottom: 6,
      marginTop: 4,
    },
    urlTypeBlock: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: 12,
    },
    urlType: {
      width: '100%',
    },
    input: {
      marginBottom: 12,
    },
    conditionBlock: {
      backgroundColor: color.$19,
      borderRadius: 24,
      padding: '16px 12px',
    },
    viewBlock: {
      fontSize: '15px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$13,
      display: 'flex',
      alignItems: 'center',
      marginBottom: 12,
    },
    view: {
      marginRight: 8,
      width: 80,
    },
    radio: {
      fontWeight: '400',
      fontSize: '15px',
      lineHeight: '15px',
      color: color.$13,
    },
    option: {
      marginRight: 36,
    },
    radioInput: {
      fontSize: 15,
    },
    dropdown: {
      margin: '0 8px',
      width: 80,
    },
    stay: {
      display: 'flex',
      alignItems: 'center',
      fontWeight: '400',
      fontSize: '15px',
      lineHeight: '15px',
      color: color.$13,
    },
  },
  {
    name: 'GAViewCondition',
  },
);

const GAViewCondition = ({
  className,
  condition,
  isError,
  onChangeCondition,
  isShowDelete,
  onDelete,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [radioType, setRadioType] = useState('off');
  const [minute, setMinute] = useState(0);
  const [second, setSecond] = useState(0);
  const { urlType, url, pageViewTimes, staySec } = condition || {};

  const urlFilterItems = [
    {
      text: t(`GA.${UrlFilterType.EQ}`),
      value: UrlFilterType.EQ,
    },
    {
      text: t(`GA.${UrlFilterType.IN}`),
      value: UrlFilterType.IN,
    },
    {
      text: t(`GA.${UrlFilterType.NIN}`),
      value: UrlFilterType.NIN,
    },
  ];

  const viewItem = generateDateItem(9, 1, 1);

  const minuteItem = generateDateItem(10);

  const secondItem = generateDateItem(60, 5);

  const onChangeUrlType = value => {
    onChangeCondition({
      urlType: value,
    });
  };

  const onChangeUrl = value => {
    onChangeCondition({
      url: value,
    });
  };

  const onChangeView = value => {
    onChangeCondition({
      pageViewTimes: value,
    });
  };

  const onChangeMinute = value => {
    onChangeCondition({
      staySec: value * 60 + second,
    });
  };

  const onChangeSecond = value => {
    onChangeCondition({
      staySec: minute * 60 + value,
    });
  };

  const onChangeRadioType = value => {
    if (value === 'on') {
      onChangeCondition({
        staySec: 5,
      });
    } else {
      onChangeCondition({
        staySec: null,
      });
    }
  };

  useEffect(() => {
    setRadioType(staySec === null ? 'off' : 'on');
    if (staySec !== null) {
      setMinute(changeSeconds(staySec, 'minute'));
      setSecond(changeSeconds(staySec, 'second'));
    }
  }, [staySec]);

  return (
    <div className={classnames(className, classes.conditionBlock)}>
      <div className={classes.urlTypeBlock}>
        <Dropdown
          className={classes.urlType}
          menuItems={urlFilterItems}
          value={urlType}
          onChange={onChangeUrlType}
        />
        {isShowDelete && (
          <div style={{ marginLeft: 8 }} onClick={onDelete}>
            <IconX16 className={classes.iconSvg} />
          </div>
        )}
      </div>
      <TextField
        className={classes.input}
        type="text"
        isValid
        placeholder={t(`GA.${urlType}UrlPlaceholder`)}
        value={url}
        onChange={event => onChangeUrl(event?.target?.value)}
        isError={isError && !(url && validateHttpsAndHttpPath(url))}
      />
      <div className={classes.subtitle}>{t('GA.view')}</div>
      <div className={classes.viewBlock}>
        <Dropdown
          className={classes.view}
          menuItems={viewItem}
          value={pageViewTimes}
          onChange={onChangeView}
        />
        次
      </div>
      <div className={classes.subtitle}>{t('GA.stay')}</div>
      <FormControl component="fieldset">
        <RadioGroup
          aria-label="default-display"
          name="default-display"
          value={radioType}
          onChange={e => onChangeRadioType(e?.target?.value)}
        >
          <FormControlLabel
            classes={{
              label: classes.radioInput,
            }}
            value="on"
            control={<Radio color="primary" />}
            label={
              <div className={classes.stay}>
                {t('GA.stay1')}
                <Dropdown
                  className={classes.dropdown}
                  menuItems={minuteItem}
                  value={minute}
                  onChange={onChangeMinute}
                  disabled={staySec === null}
                />
                {t('messages.minute')}
                <Dropdown
                  className={classes.dropdown}
                  menuItems={secondItem}
                  value={second}
                  onChange={onChangeSecond}
                  disabled={staySec === null}
                />
                {t('messages.second')}
              </div>
            }
          />
          <FormControlLabel
            classes={{
              label: classes.radioInput,
            }}
            value="off"
            control={<Radio color="primary" />}
            label={<div className={classes.radio}>{t('GA.stayOff')}</div>}
          />
        </RadioGroup>
      </FormControl>
    </div>
  );
};

export default GAViewCondition;
